import React, { useCallback, useEffect, useState } from 'react';
import { AppButton, AppEmptyState, AppSpinner } from '../../components/ui';
import { AppFilterBar, AppFilterInput } from '../../components/filters';
import { notify } from '../../components/common/AppToast';
import useAppConfirm from '../../hooks/useAppConfirm';
import clientPropositionService from '../../services/clientPropositionService';
import { hubspotCompanyHref } from '../../utils/clientIdentifiants';

const ClientPropositionsPanel = ({ source }) => {
  const { ask, modal } = useAppConfirm();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ high_score: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clientPropositionService.list({ source, q: debounced, limit: 300 });
      setRows(data.propositions);
      setCounts(data.counts);
    } catch (err) {
      notify.error(err.message || 'Impossible de charger les propositions');
    } finally {
      setLoading(false);
    }
  }, [source, debounced]);

  useEffect(() => {
    load();
  }, [load]);

  const runProposer = async () => {
    setBusy(true);
    try {
      const stats = await clientPropositionService.proposer(source);
      const proposed = stats?.[source]?.proposed ?? 0;
      notify.success(`${proposed} proposition(s) ${source} générée(s).`);
      await load();
    } catch (err) {
      notify.error(err.message || 'Proposition automatique impossible');
    } finally {
      setBusy(false);
    }
  };

  const acceptOne = async (id) => {
    setBusyId(id);
    try {
      await clientPropositionService.accept(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      notify.success('Identifiant rattaché');
    } catch (err) {
      notify.error(err.message || 'Validation impossible');
    } finally {
      setBusyId(null);
    }
  };

  const rejectOne = async (id) => {
    setBusyId(id);
    try {
      await clientPropositionService.reject(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      notify.success('Proposition rejetée');
    } catch (err) {
      notify.error(err.message || 'Rejet impossible');
    } finally {
      setBusyId(null);
    }
  };

  const acceptHigh = async () => {
    const ok = await ask({
      title: 'Valider les propositions ≥ 95 % ?',
      message: `${counts.high_score || 0} ligne(s) seront rattachées.`,
      confirmLabel: 'Valider',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const stats = await clientPropositionService.acceptLot({ source, minScore: 0.95 });
      notify.success(`${stats.accepted || 0} identifiant(s) rattaché(s).`);
      await load();
    } catch (err) {
      notify.error(err.message || 'Validation groupée impossible');
    } finally {
      setBusy(false);
    }
  };

  const label = source === 'hubspot' ? 'HubSpot' : 'RAG';

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <AppButton size="sm" icon="mdi:magic-staff" loading={busy} onClick={runProposer}>
          Proposer {label}
        </AppButton>
        <AppButton
          size="sm"
          variant="outline-success"
          disabled={!counts.high_score}
          loading={busy}
          onClick={acceptHigh}
        >
          Valider ≥ 95 % ({counts.high_score || 0})
        </AppButton>
      </div>

      <AppFilterBar className="mb-3 shadow-none border" onReset={() => setQuery('')} resetDisabled={!query}>
        <AppFilterInput
          id={`prop-search-${source}`}
          label="Rechercher"
          placeholder="Client ou candidat"
          value={query}
          onChange={setQuery}
        />
      </AppFilterBar>

      {loading ? (
        <AppSpinner label="Chargement des propositions…" />
      ) : rows.length === 0 ? (
        <AppEmptyState
          message={`Aucune proposition ${label}. Lancez une passe, ou rattachez un identifiant depuis la fiche client.`}
        />
      ) : (
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Client Dolibarr</th>
                <th>Candidat {label}</th>
                <th>Score</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="fw-medium">{row.client?.nom}</div>
                    <div className="text-secondary-light small">
                      #{row.client?.dolibarr_thirdparty_id}
                      {row.client?.dolibarr_code ? ` · ${row.client.dolibarr_code}` : ''}
                    </div>
                  </td>
                  <td>
                    <div className="fw-medium">{row.libelle_propose}</div>
                    <div className="text-secondary-light small">{row.identifiant_propose}</div>
                    {source === 'hubspot' && hubspotCompanyHref(row.identifiant_propose) ? (
                      <AppButton
                        as="a"
                        variant="link"
                        size="sm"
                        className="px-0"
                        href={hubspotCompanyHref(row.identifiant_propose)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ouvrir HubSpot
                      </AppButton>
                    ) : null}
                  </td>
                  <td>{row.score != null ? `${Math.round(row.score * 100)} %` : '—'}</td>
                  <td className="text-end">
                    <div className="d-inline-flex gap-1">
                      <AppButton
                        size="sm"
                        variant="outline-success"
                        disabled={busyId === row.id}
                        onClick={() => acceptOne(row.id)}
                      >
                        Valider
                      </AppButton>
                      <AppButton
                        size="sm"
                        variant="outline-danger"
                        disabled={busyId === row.id}
                        onClick={() => rejectOne(row.id)}
                      >
                        Rejeter
                      </AppButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal}
    </div>
  );
};

export default ClientPropositionsPanel;
